<template>
  <div class="p-the-template-j">
    <TheBreadcrumb />
    <div class="p-the-template-j__inner">
      <div class="p-the-template-j__header">
        <slot name="header">
          <CommonHeading v-if="heading">{{ heading }}</CommonHeading>
        </slot>
      </div>
      <div class="p-the-template-j__body">
        <div class="p-the-template-j__body__header">
          <slot name="beforeSearch" />
          <div
            ref="searchFormElement"
            class="p-the-template-j__search"
            :class="executeSearchFormDomAnimation ? searchFormClassName : ''"
            :style="executeSearchFormDomAnimation ? serchFormStyle : {}"
          >
            <FormSearch
              class="p-the-template-j__form-search"
              v-model:search="search"
              :placeholder="searchPlaceholder"
              :button-color="searchButtonColor"
              :class="{
                'c-search-input--empty': !searchKeyword.trim()
              }"
              @do-search="doSearch"
              @click="showSearchSuggest"
            />
            <div
              class="p-search-help-wrapper"
              @mouseenter="showTooltipPc"
              @mouseleave="hideTooltipPc"
            >
              <div
                class="p-search-help"
                @click.stop="toggleTooltip"
              >
                ⓘ
              </div>
              <div
                v-if="showTooltip && !isSp"
                class="p-search-help__tooltip"
                @click.stop
              >
                グループ名またはグループコード、氏名、IDで検索できます。
              </div>
            </div>

            <div
              v-if="showTooltip && isSp"
              class="p-help-modal-overlay"
              @click="showTooltip = false"
            >
              <div
                class="p-help-modal"
                @click.stop
              >
                <div class="p-help-modal__body">
                  グループ名またはグループコード、氏名、IDで検索できます。
                </div>
              </div>
            </div>
            <slot name="searchSuggest" />
          </div>
        </div>
        <div class="p-search-error">
          <ValidationErrorText v-show="errorMessage">
            {{ errorMessage }}
          </ValidationErrorText>
        </div>
        <div
          ref="contentElement"
          class="p-the-template-j__body__content"
          :style="contentElementStyle"
        >
          <slot name="contents" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { gsap } from "gsap";
import TheStickyFooterButton from "@/common/components/organisms/TheStickyFooterButton.vue";
import FormSearch from "@/common/components/atoms/FormSearch.vue";
import ButtonRefine from "@/common/components/atoms/ButtonRefine.vue";
import ButtonRefineBase from "@/common/components/atoms/ButtonRefineBase.vue";
import ValidationErrorText from "@/common/components/atoms/ValidationErrorText.vue";
import { Options, Vue } from "vue-class-component";
import globals from "@/general/store/modules/globals";
import modalState from "@/common/store/modules/modalState";
import { getThanksPointName } from "@/common/assets/js/utils/pointName";
import CONST_DATA from "@/general/mixins/const/constData";

@Options({
  props: {
    heading: { type: [String, Boolean], default: false, required: true },
    indicatedItem: { type: Array, default: ["search", "button"] },
    searchPlaceholder: { type: String, default: "検索" },
    initShownSearchFormState: { type: Boolean, default: false },
    sorted: {
      type: Object,
      default: () => ({
        target: "username-asc",
        shownState: false,
        itemList: []
      })
    },
    filterHeading: { type: String, default: "並べ替え" },
    errorMessage: { type: String, default: null },
    searchButtonColor: {
      type: String,
      default: ""
    }
  },
  components: {
    TheStickyFooterButton,
    FormSearch,
    ButtonRefine,
    ButtonRefineBase,
    ValidationErrorText
  },
  watch: {
    searchKeyword(newVal: string) {
      this.$emit("search-change", newVal);
    },
    isSp(newVal: boolean) {
      this.initSearchFormDomAnimation();
    },
    shownSearchFormState(newVal: boolean) {
      this.animationSearchFormDom(this.$refs.searchFormElement);
    }
  }
})
export default class TheTemplateJ extends Vue {
  declare $refs: {
    contentElement: HTMLElement;
    searchFormElement: HTMLElement;
  };

  showTooltip = false;
  heading!: string;
  indicatedItem!: Array<string>;
  searchPlaceholder!: string;
  initShownSearchFormState!: string;
  sorted!: any;
  filterHeading!: string;
  errorMessage!: string;

  searchKeyword  = "";
  closeModalState = modalState.closeModalState;

  fadeOutState = false;
  searchUriBase = ["/j03/", "/j04/"];

  executeSearchFormDomAnimation = false;
  shownSearchFormState = true;
  searchFormClassName = "p-the-template-j__search--animation";

  serchFormStyle = {
    visibility: "hidden",
    opacity: 0
  };

  searchButtonColor!: string;

  get search() {
    return this.searchKeyword;
  }

  set search(value) {
    this.searchKeyword = value;
  }

  get indicateSearchButton() {
    const indicatedItem = this.indicatedItem;
    return indicatedItem.includes("searchButton");
  }

  get indicateSortedButton() {
    const indicatedItem = this.indicatedItem;
    const keyword = "sorted";
    return indicatedItem.includes(keyword);
  }

  get contentElementStyle() {
    const executeSearchFormDomAnimation = this.executeSearchFormDomAnimation;
    let styleObject = {};

    if (executeSearchFormDomAnimation === false) {
      styleObject = {
        marginTop: 0
      };
    }

    return styleObject;
  }

  get widthRefineButton() {
    return "170px";
  }

  get thanksPointName() {
    return getThanksPointName(CONST_DATA.POINT_NAME.THANKS_POINT_DEFAULT_NAME1);
  }

  mounted() {
    this.initDomElement();

    document.addEventListener(
      "click",
      this.closeTooltip
    );
  }

  beforeUnmount() {
    document.removeEventListener(
      "click",
      this.closeTooltip
    );
  }

  closeTooltip() {
    this.showTooltip = false;
  }

  initDomElement() {
    const initShownSearchFormState = this.initShownSearchFormState;
    this.initSearchFormDomAnimation();

    if (initShownSearchFormState) {
      this.animationSearchFormDom(this.$refs.searchFormElement);
    }
  }

  fadeOutNewAddButton() {
    const scrollY = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const triggerPosition = documentHeight - windowHeight;

    this.fadeOutState = scrollY === triggerPosition;
  }

  doSearch() {
    this.$emit("do-search", this.search);
  }

  doSort($event: any) {
    this.$emit("do-sort", $event);
  }

  switchSortedState(value: any) {
    this.sorted.shownState = value;
  }

  switchSearchFormState() {
    this.shownSearchFormState = !this.shownSearchFormState;
  }

  get isSp() {
    return globals.isLargeBreakpointWidthOfSp;
  }

  initSearchFormDomAnimation() {
    const isSp = this.isSp;
    const indicateSearchButton = this.indicateSearchButton;
    const executeSearchFormDomAnimation = isSp && indicateSearchButton;

    this.executeSearchFormDomAnimation = executeSearchFormDomAnimation;
    this.shownSearchFormState = !executeSearchFormDomAnimation;
  }

  animationSearchFormDom(el: any, shown = this.shownSearchFormState) {
    const executeSearchFormDomAnimation = this.executeSearchFormDomAnimation;
    const { contentElement } = this.$refs;
    const speed = 0.2;

    const animationStyle = {
      formSearchElement: {
        autoAlpha: shown ? 1 : 0
      },
      contentElement: {
        marginTop:
          shown && executeSearchFormDomAnimation ? `${60 / 16}rem` : "0rem"
      }
    };

    gsap.to(el, speed, animationStyle.formSearchElement);
    gsap.to(contentElement, speed, animationStyle.contentElement);
  }

  showSearchSuggest() {
    this.$emit("show-search-suggest");
  }
  toggleTooltip() {
    if (this.isSp) {
      this.showTooltip = !this.showTooltip;
    }
  }

  showTooltipPc() {
    if (!this.isSp) {
      this.showTooltip = true;
    }
  }

  hideTooltipPc() {
   if (!this.isSp) {
     this.showTooltip = false;
    }
  }
}
</script>

<style lang="scss" scoped>
.p-the-template-j {
  &__inner {
    @include pc {
      max-width: rem(1110);
      margin: 0 auto;
      padding: 0 0 rem(48);
    }
  }

  &__search {
    @include pc {
      width: rem(288);
      min-width: rem(288);
    }

    @include sp {
      padding-bottom: rem(16);
    }

    &--animation {
      @include sp {
        width: 100%;
        position: absolute;
        padding-top: rem(60);
      }
    }
  }

  &__new-add {
    width: rem(280);
    min-width: rem(280);
    margin: 0 0 0 auto;
  }

  &__right-col {
    @include sp {
      display: flex;
      flex-direction: flex-end;
      justify-content: space-between;
      position: relative;
      z-index: 1;
    }
  }

  &__shown-search-form-button {
    @include pc {
      display: none;
    }
    @include sp {
      width: cal(136, 288);
      min-width: cal(136, 288);
    }

    &__button {
      @include sp {
        display: block;
        width: 100%;
      }
    }
  }

  &__sorted {
    @include pc {
      width: rem(168);
      min-width: rem(168);
    }
    @include sp {
      width: cal(136, 288);
      min-width: cal(136, 288);
    }
  }

  &__body {
    @include pc {
      margin-top: rem(8);
      padding: rem(24) rem(24) rem(48);
    }
    @include sp {
      padding: rem(24) 0 rem(48);
    }
    background-color: $common-white;
    border-radius: rem(4);

    &__header {
      position: relative;
    }
  }

  &__give-button {
    overflow-wrap: break-word;
  }
}

.p-the-template-j__search-wrapper {
  position: relative;
}

.p-the-template-j__search {
  position: relative;

  @include pc {
    width: 100%;
    max-width: rem(576);
    margin: 0 auto;
    margin-top: 16px;
  }

  @include sp {
    padding-bottom: rem(16);
  }
}

.p-search-help-wrapper {
  @include pc {
    position: absolute;
    left: calc(100% + 8px);
    top: 50%;
    transform: translateY(-50%);
    z-index: 20;
  }
}

.p-search-help {
  color: #6264a7;
  font-size: 16px;
  cursor: pointer;

  @include sp {
    position: absolute;
    right: 0;
    left: auto;
    top: -20px;
  }
}

.p-search-help__tooltip {
  position: absolute;

  width: 240px;
  padding: 8px 12px;

  background: #333;
  color: #fff;

  border-radius: 4px;

  font-size: 12px;
  line-height: 1.5;

  z-index: 2000;

  @include pc {
    left: calc(100% + 8px);
    top: 0;
  }

  @include sp {
    left: 0;
    top: calc(100% + 5px);
  }
}

.c-search-input--empty {
  background-color: rgb(255 205 231 / 33%);
}

.p-search-error {
  width: rem(576);
  margin: 8px auto 0;
}

:deep(.p-the-template-j__form-search .p-form-search__button) {
  background: #6264a7;
}

:deep(.p-the-template-j__form-search .p-form-search__button svg) {
  color: #fff;
}
.p-help-modal-overlay {
  position: fixed;
  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  background: rgba(0, 0, 0, .4);

  z-index: 3000;
}

.p-help-modal {
  width: 300px;
  max-width: calc(100vw - 32px);

  background: #fff;
  border-radius: 8px;

  overflow: hidden;

  box-shadow: 0 8px 24px rgba(0, 0, 0, .2);
}

.p-help-modal__header {
  padding: 16px;

  font-size: 16px;
  font-weight: 700;

  border-bottom: 1px solid #eee;
}

.p-help-modal__body {
  padding: 12px;

  line-height: 1.8;
}

.p-help-modal__footer {
  padding: 16px;

  text-align: right;

  border-top: 1px solid #eee;
}

.p-help-modal__button {
  min-width: 100px;
  height: 40px;

  border: none;
  border-radius: 20px;

  background: #6264a7;
  color: #fff;
}
</style>
